import { Button, DatePicker, Pagination, Select, Space, TableColumnsType, Tabs } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";

import { ContentWrapper } from "../../Components/Layout/ContentWrapper";
import { DataTable } from "../../Components/DataTable/DataTable";
import { getColoriezedValue } from "../../Utils/getColorizedValue";
import { getFormattedDate, getKey } from "../../Utils/CommonUtils";
import { columnWidth } from "../../Components/DataTable/Properties";
import { useNotification } from "../../Hooks/useNotification";
import { panelUrl, toArray, workOrderApi } from "../../Services";

import { approvalStatusOptions, workOrderStatusLabel } from "../WorkOrder/Constants";
import { WorkOrderFilter, WorkOrderFilters, filterParams } from "../WorkOrder/WorkOrderFilter";
import styles from "../WorkOrder/WorkOrder.module.scss";

const { RangePicker } = DatePicker;

const PENDING = "pendingApproval";
const APPROVED = "approved";

/** A work order as its row reads — shared by the table and its Export. */
const toRow = (item: any) => ({
  ...item,
  costCentre: item.division ?? item.costCentre,
  workCategory: item.category ?? item.workCategory,
  commencementDate: getFormattedDate(item.commencementDate) || "-",
  completionDate: getFormattedDate(item.completionDate) || "-",
});

/**
 * Work Order Approval — approver-facing list.
 *
 * Mirrors ReviewSPA's relationship to SPA: a separate list component with
 * Pending/Approved tabs, sharing the detail screen with the Work Order module
 * via a different route (`/workorder-approval/view/:id`). The drilldown reads
 * the path to decide whether to show approver actions.
 *
 * There is no "Add" here — approvers act on existing work orders.
 */
export const WorkOrderApproval = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsObj = Object.fromEntries(searchParams.entries());
  /* Search runs on the server, as on the Work Order list: `searchText` was
     already read off the URL and sent to the endpoint, but nothing ever wrote
     it, so the box only filtered the page already on screen. */
  const [clearSearchInput, setClearSearchInput] = useState(false);
  /* The debounced callback is built once and must not capture stale params. */
  const paramsRef = useRef(paramsObj);
  paramsRef.current = paramsObj;

  const runSearch = useMemo(
    () =>
      debounce((value: string) => {
        const next: Record<string, string> = { ...paramsRef.current, page: "0" };
        if (value) next.searchText = value;
        else delete next.searchText;
        setSearchParams(next);
      }, 500),
    [setSearchParams]
  );

  const activeTab = searchParams.get("activeTab") || PENDING;
  const pageParam = Number(searchParams.get("page"));
  const page = !isNaN(pageParam) ? pageParam : 0;
  const searchText = searchParams.get("searchText") ?? "";
  const status = searchParams.get("status") ?? "";
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");

  const { openNotification } = useNotification();
  const [data, setData] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [recordsPerPage] = useState<number>(10);
  const [isLoading, setIsLoading] = useState(false);
  /* The queue request, re-run page by page by DataTable's own Export — the
     Manage Catalog pattern, so "All" is every page, not the ten on screen. */
  const [exportUrl, setExportUrl] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  /* The list endpoints sort on createdOn — see Utils/sortField. */
  const [orderByName, setOrderByName] = useState("createdOn");

  const [pickerKey, setPickerKey] = useState(0);
  const dateRangeValue: any =
    startDate && endDate ? [moment(startDate), moment(endDate)] : null;

  const readList = (key: string): string[] => {
    const raw = searchParams.get(key);
    return raw ? raw.split(",").filter(Boolean) : [];
  };

  const activeFilters: WorkOrderFilters = {
    procurementApproach: readList("procurementApproach"),
    category: readList("category"),
    subsidiary: readList("subsidiary"),
    costCentre: readList("costCentre"),
  };

  const applyFilters = (filters: WorkOrderFilters) => {
    const next: Record<string, string> = { ...paramsObj, page: "0" };
    (Object.keys(filters) as (keyof WorkOrderFilters)[]).forEach((key) => {
      if (filters[key].length) {
        next[key] = filters[key].join(",");
      } else {
        delete next[key];
      }
    });
    setSearchParams(next);
  };

  useEffect(() => {
    if (!searchParams.get("activeTab")) {
      setSearchParams({ activeTab: PENDING, page: "0" }, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const getRecords = (currentPage: number = page) => {
    setIsLoading(true);

    /* /work-order/approve/list: `isCompleted` separates the tabs. `ownerEmail`
       (the signed-in approver) is left out for now, on request, so the queue
       is whatever the endpoint returns unscoped. `status` is sent only when
       one is picked — the Approved tab used to force `status=approved`, a
       lower-case value the endpoint's codes (PENDING, …) never match. */
    const params = {
      isCompleted: activeTab === APPROVED,
      page: currentPage,
      size: recordsPerPage,
      searchText: searchText || undefined,
      order: sortOrder,
      orderBy: orderByName,
      status: status || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      ...filterParams(activeFilters),
    };
    setExportUrl(panelUrl("/work-order/approve/list", params));
    workOrderApi
      .approveList(params)
      .then((res: any) => {
        setData(
          toArray(res).map((item: any, index: number) => ({
            ...toRow(item),
            key: getKey(currentPage, index),
          }))
        );
        setTotalRecords(res?.totalCount ?? 0);
      })
      .catch(() => {
        setData([]);
        setTotalRecords(0);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getRecords(page);
    // eslint-disable-next-line
  }, [
    activeTab,
    page,
    searchText,
    status,
    startDate,
    endDate,
    sortOrder,
    orderByName,
    activeFilters.procurementApproach.join(","),
    activeFilters.category.join(","),
    activeFilters.subsidiary.join(","),
    activeFilters.costCentre.join(","),
  ]);

  const handleTableSort = (column: any, order: string) => {
    setOrderByName(column.dataIndex);
    setSortOrder(order === "asc" ? "desc" : "asc");
  };

  useEffect(() => () => runSearch.cancel(), [runSearch]);

  /* The box clears the moment they type; only the request waits for a pause. */
  const handleSearchChange = (value: string) => {
    setClearSearchInput(false);
    runSearch(value);
  };

  /* Clearing the picker drops both dates from the URL. They used to be
     overwritten only when set, so the old range stayed in the URL and the list
     stayed filtered — empty — after the dates were cleared. */
  const setDate = (dates: any) => {
    const { startDate: _start, endDate: _end, ...rest } = paramsObj;
    setSearchParams({
      ...rest,
      ...(dates?.startDate && { startDate: dates.startDate }),
      ...(dates?.endDate && { endDate: dates.endDate }),
      page: "0",
    });
  };

  const columns: TableColumnsType = [
    { title: "Work Order No.", dataIndex: "workOrderNo", sorter: true, width: columnWidth.GENERAL },
    { title: "Job Title", dataIndex: "jobTitle", sorter: true, width: columnWidth.PROJECT_NAME },
    {
      title: "Subsidiary/Division",
      dataIndex: "subsidiary",
      sorter: true,
      width: columnWidth.DEPARTMENT_NAME,
    },
    { title: "Cost Centre", dataIndex: "costCentre", sorter: true, width: columnWidth.DESIGNATION },
    { title: "Vendor Name", dataIndex: "vendorName", sorter: true, width: columnWidth.DESIGNATION },
    {
      title: "Work Category",
      dataIndex: "workCategory",
      sorter: true,
      width: columnWidth.DEPARTMENT_NAME,
    },
    { title: "Region", dataIndex: "region", sorter: true, width: columnWidth.DESIGNATION },
    {
      title: "Status",
      dataIndex: "status",
      sorter: true,
      width: columnWidth.GENERAL,
      /* The API's code (PENDING_REVIEW) as people read it ("Created"). */
      render: (value: string) => getColoriezedValue(workOrderStatusLabel(value).toLowerCase()),
    },
    {
      title: "Commencement Date",
      dataIndex: "commencementDate",
      sorter: true,
      width: columnWidth.DATE,
    },
    {
      title: "Completion Date",
      dataIndex: "completionDate",
      sorter: true,
      width: columnWidth.DATE,
    },
  ];


  const table = (
    <>
      {/* Keyed by tab: both tabs share this one table, so without a fresh
          mount it kept the sort arrow (and box text) from the other tab. */}
      <DataTable
        key={activeTab}
        columns={columns.map((col: any) => ({
          ...col,
          onHeaderCell: (column: any) => ({
            onClick: () => {
              if (column.sorter) {
                handleTableSort(column, sortOrder);
              }
            },
          }),
        }))}
        className="table-clickable"
        tableData={data}
        isLoading={isLoading}
        hideExport={false}
        exportFileName="Work Order Approval"
        exportApiUrl={exportUrl}
        exportRowMapper={(record: any) => ({ ...toRow(record), status: workOrderStatusLabel(record.status) })}
        /* `from=approved`: opened from the Approved tab — the detail page shows no
           decision buttons and its back arrow returns to that tab. */
        onClick={(record) =>
          navigate(
            `/workorder-approval/view/${encodeURIComponent(record.envelopeId ?? record.workOrderNo)}${
              activeTab === APPROVED ? "?from=approved" : ""
            }`
          )
        }
        additionalCTA={
          <Space
            wrap
            style={{ width: "100%", justifyContent: "flex-end" }}
          >
            <RangePicker
              /* A fresh picker per opening: rc-picker remembers the month you
                 browsed to and has no prop to steer it, so it reopened on e.g.
                 Oct 2015. New, it starts on today — or on the chosen range. */
              key={pickerKey}
              onOpenChange={(open) => !open && setPickerKey((key) => key + 1)}
              value={dateRangeValue}
              format="DD MMM YYYY"
              placeholder={["From Date", "To Date"]}
              onChange={(dates: any) =>
                setDate({
                  startDate: dates?.[0] ? dates[0].format("YYYY-MM-DD") : "",
                  endDate: dates?.[1] ? dates[1].format("YYYY-MM-DD") : "",
                })
              }
            />
            <Select
              style={{ minWidth: 160 }}
              placeholder="Select Status"
              value={status || undefined}
              allowClear={!!status}
              getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
              onChange={(value: string) =>
                setSearchParams({ ...paramsObj, status: value ?? "", page: "0" })
              }
            >
              {approvalStatusOptions[activeTab === APPROVED ? "approved" : "pending"].map((option) => (
                <Select.Option
                  value={option.value}
                  key={option.value}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            <WorkOrderFilter
              value={activeFilters}
              onApply={applyFilters}
            />
          </Space>
        }
        totalRecords={totalRecords}
        inputDisable={isLoading}
        isPagination={true}
        onSearchChange={handleSearchChange}
        isServerSearchApi
        clearStringSearch={clearSearchInput}
        searchPlaceholderText="Search by Work order no./ Job title"
        recordsPerPage={recordsPerPage}
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={page + 1}
          total={totalRecords}
          onChange={(nextPage) => setSearchParams({ ...paramsObj, page: String(nextPage - 1) })}
          showSizeChanger={false}
        />
      </div>
    </>
  );

  const items = [
    { key: PENDING, label: "Pending for Approval", children: table },
    { key: APPROVED, label: "Approved", children: table },
  ];

  return (
    <ContentWrapper
      title="Work Order Approval"
    >
      <div className={styles.pillTabs}>
        <Tabs
          items={items}
          activeKey={activeTab}
          onChange={(key) => {
            /* A tab starts clean — setSearchParams replaces the whole query,
               so the filters, dates and status go with it; the search box
               and the sort live here and are reset by hand. */
            runSearch.cancel();
            setClearSearchInput(true);
            setSortOrder("desc");
            setOrderByName("createdOn");
            setSearchParams({ activeTab: key, page: "0" });
          }}
        />
      </div>
    </ContentWrapper>
  );
};

